import React from 'react'
import { styled } from 'baseui'

export type StackDirection = 'column' | 'row'
export type StackAlign = 'start' | 'center' | 'end' | 'stretch'
export type StackJustify = 'start' | 'center' | 'end' | 'space-between'

export interface StackProps {
  /** Default: 'column'. */
  direction?: StackDirection
  /** Gap between children, in px. */
  gap?: number
  align?: StackAlign
  justify?: StackJustify
  /** Stretches to the container's width. */
  fullWidth?: boolean
  /** CSS padding shorthand — for frame components that own their own inset. */
  padding?: string
  background?: string
  /** Corner radius in px. */
  radius?: number
  width?: number
  height?: number
  children?: React.ReactNode
}

const FLEX_ALIGN: Record<StackAlign, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
}

const FLEX_JUSTIFY: Record<StackJustify, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  'space-between': 'space-between',
}

const StackRoot = styled(
  'div',
  (props: {
    $direction: StackDirection
    $gap?: number
    $align?: StackAlign
    $justify?: StackJustify
    $fullWidth?: boolean
    $padding?: string
    $background?: string
    $radius?: number
    $width?: number
    $height?: number
  }) => ({
    display: 'flex',
    boxSizing: 'border-box',
    flexDirection: props.$direction,
    ...(props.$gap != null ? { gap: `${props.$gap}px` } : {}),
    ...(props.$align ? { alignItems: FLEX_ALIGN[props.$align] } : {}),
    ...(props.$justify ? { justifyContent: FLEX_JUSTIFY[props.$justify] } : {}),
    ...(props.$fullWidth ? { width: '100%' } : {}),
    ...(props.$width != null ? { width: `${props.$width}px` } : {}),
    ...(props.$height != null ? { height: `${props.$height}px` } : {}),
    ...(props.$padding ? { padding: props.$padding } : {}),
    ...(props.$background ? { background: props.$background } : {}),
    ...(props.$radius != null ? { borderRadius: `${props.$radius}px` } : {}),
  }),
)

/**
 * Layout primitive: a flex row/column with a gap. Replaces the ad-hoc
 * `<div style={{ display: 'flex', flexDirection, gap }}>` wrappers that Figma
 * auto-layout frames translate into.
 *
 * Presentation only — no colors or typography beyond an optional background.
 */
function Stack({
  direction = 'column',
  gap,
  align,
  justify,
  fullWidth = false,
  padding,
  background,
  radius,
  width,
  height,
  children,
}: StackProps) {
  return (
    <StackRoot
      $direction={direction}
      $gap={gap}
      $align={align}
      $justify={justify}
      $fullWidth={fullWidth}
      $padding={padding}
      $background={background}
      $radius={radius}
      $width={width}
      $height={height}
    >
      {children}
    </StackRoot>
  )
}

export default Stack
